import {
    BackendClient,
    BlobUploadType,
    RequestParameters,
    STATUS_REQUEST_TIMEOUT_MS,
} from './backend-client';
import {
    createOutageTolerance,
    HttpStatusError,
    POLL_OUTAGE_TOLERANCE_MS,
} from './network-retry';
import { isAbortError } from './retryOn202';
import { handleError } from '~/utils/errors';

export const DEFAULT_POLL_INTERVAL_MS = 10000;
export const DEFAULT_TIMEOUT_MS = 500000;

export type DatasetUploadResult = {
    /** True when the backend accepted the file. */
    uploaded: boolean;
    /**
     * Number of files the backend reports the dataset holding after
     * this upload (`actual_amount`); undefined on backends that do not
     * report it.
     */
    datasetFileCount?: number;
    /**
     * True when the upload needed more than one attempt, so the file may
     * have been delivered twice.
     */
    retried: boolean;
};

/**
 * Shared base for task-family v2 backend clients. Owns the pieces
 * every family has in common:
 *
 * - `/v2/images` dataset endpoints (create dataset, upload slice) are
 *   task-family-agnostic; the backend keys them by ID, not by task
 *   type, so the same methods serve registration, composite-CBCT and
 *   any future family.
 * - Task lifecycle endpoints (`/v2/{taskPathSegment}/{task_id}` and
 *   its `/results/{result_id}` child) are structurally identical
 *   across families — same polling loop, same result-blob download,
 *   same delete — so they live here, parameterised by the
 *   `taskPathSegment` each subclass supplies.
 *
 * Subclasses add only the family-specific task-creation method,
 * because that's the one place where the request body differs (reg
 * takes a transformation matrix; composite-CBCT does not).
 */
export abstract class BackendV2Client extends BackendClient {
    /**
     * URL segment that identifies this task family, plugged into
     * `/v2/{segment}/{task_id}` etc. `'reg'` for registration,
     * `'composite_cbct'` for composite CBCT.
     */
    protected abstract get taskPathSegment(): string;

    /**
     * Creates a new image dataset on the backend and returns the
     * response JSON (which carries the dataset `id` used as a
     * reference in the task-creation payload).
     */
    public async createImageDataset(
        request: RequestParameters
    ): Promise<unknown> {
        try {
            // One attempt (the POST default): every create mints a new
            // dataset id, and a replay after a lost response would leave a
            // second, orphaned dataset the client never tracks.
            const response = await this.request('v2/images', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                buildBody: () =>
                    JSON.stringify({
                        expected_amount: Number(request.nExpectedFiles),
                    }),
                params: { sessionId: request.sessionId ?? this.sessionID },
            });

            if (!response.ok) {
                const text = await response.text();
                handleError(
                    new Error(`Failed to create dataset: ${response.status}`),
                    text
                );
                return false;
            }

            return response.json();
        } catch (error) {
            this.reportRequestFailure(error, 'Error creating V2 dataset.');
            return false;
        }
    }

    /**
     * Uploads one slice blob into an existing dataset. Returns whether
     * the backend accepted the file, the file count it reports for the
     * dataset, and whether the upload was retried, so the dataset can
     * be verified against its declared count before it starts a task.
     */
    public async uploadFileToDataset(
        request: RequestParameters,
        blob: BlobUploadType,
        datasetID: string,
        idx: number
    ): Promise<DatasetUploadResult> {
        let retried = false;
        try {
            const fileName = datasetID + '_' + idx + '.dcm';

            const response = await this.request(`v2/images/${datasetID}`, {
                method: 'PUT',
                label: 'v2/images/{datasetId}',
                onRetry: () => {
                    retried = true;
                },
                buildBody: () => {
                    const formData = new FormData();
                    formData.append('file', blob.file, fileName);
                    return formData;
                },
                params: { sessionId: request.sessionId ?? this.sessionID },
            });

            if (!response.ok) {
                const errorText = await response.text();
                handleError(
                    new Error(`Failed to upload file: ${response.status}`),
                    errorText
                );
                return { uploaded: false, retried };
            }

            return {
                uploaded: true,
                datasetFileCount: await readDatasetFileCount(response),
                retried,
            };
        } catch (error) {
            this.reportRequestFailure(
                error,
                'Error uploading file to dataset.'
            );
            return { uploaded: false, retried };
        }
    }

    /**
     * Polls `GET /v2/{taskPathSegment}/{taskId}` until the task
     * settles or the timeout fires. Returns the list of result IDs on
     * success, `false` on explicit backend error, on timeout, on
     * cancellation, or once the status has been unreadable for
     * {@link POLL_OUTAGE_TOLERANCE_MS}.
     */
    public async monitorTask(
        request: RequestParameters,
        taskId: string,
        pollIntervalMs: number = DEFAULT_POLL_INTERVAL_MS,
        timeoutMs: number = DEFAULT_TIMEOUT_MS
    ): Promise<{ resultIds: string[] } | false> {
        const startTime = Date.now();
        // Captured once: `abort()` swaps the controller, and this loop
        // must stop on the cancellation of the job it was started for,
        // not read a fresh, never-aborted signal at the next poll.
        const signal = this.signal;
        // The backend keeps processing whatever happens to this tab's
        // network, so a blip must not fail the job and strand its result:
        // transient failures keep the loop polling for a bounded outage.
        // Each status read is a single attempt; this interval is the
        // retry spacing.
        const outage = createOutageTolerance(POLL_OUTAGE_TOLERANCE_MS);

        while (Date.now() - startTime < timeoutMs) {
            if (signal.aborted) {
                outage.end();
                return false;
            }
            let task;
            try {
                task = await this.getTaskStatus(request, taskId);
            } catch (error) {
                if (isAbortError(error)) {
                    outage.end();
                    return false;
                }
                if (!outage.tolerate(error)) {
                    this.reportRequestFailure(
                        error,
                        'Error fetching task status.'
                    );
                    return false;
                }
                // The name only: a transport error's message can echo the
                // request.
                console.warn(
                    `Task status poll failed ` +
                        `(${error instanceof Error ? error.name : typeof error}); ` +
                        `polling on.`
                );
                await new Promise((resolve) =>
                    setTimeout(resolve, pollIntervalMs)
                );
                continue;
            }
            outage.reset();

            if (
                task &&
                task.task_status === 'PROCESSED' &&
                Array.isArray(task.results)
            ) {
                return { resultIds: task.results };
            }

            if (task && task.task_status === 'ERRORED') {
                handleError(
                    new Error('Task failed on backend'),
                    JSON.stringify(task.errors)
                );
                return false;
            }

            await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
        }

        // The overall timeout may run out mid-outage.
        outage.end();
        handleError(new Error('Timeout waiting for task results'));
        return false;
    }

    /**
     * Fetches a single result blob by id.
     */
    public async fetchTaskResult(
        request: RequestParameters,
        taskId: string,
        resultId: string
    ): Promise<Blob | false> {
        try {
            const { response, blob } = await this.requestBlob(
                `v2/${this.taskPathSegment}/${taskId}/results/${resultId}`,
                {
                    label: `v2/${this.taskPathSegment}/{taskId}/results/{resultId}`,
                    headers: { 'Content-Type': 'application/json' },
                    params: { sessionId: request.sessionId ?? this.sessionID },
                }
            );
            if (blob === undefined) {
                const text = await response.text();
                handleError(
                    new Error(
                        `Failed to fetch result file: ${response.status}`
                    ),
                    text
                );
                return false;
            }
            return blob;
        } catch (error) {
            this.reportRequestFailure(error, 'Error fetching result file.');
            return false;
        }
    }

    /**
     * Deletes the task and its associated results.
     */
    public async deleteTask(
        request: RequestParameters,
        taskId: string
    ): Promise<boolean> {
        try {
            // Safe to retry although DELETE: deletion is best-effort
            // cleanup at the end of a job, and a duplicate delete
            // reporting 404 only surfaces as a cleanup warning.
            const response = await this.request(
                `v2/${this.taskPathSegment}/${taskId}`,
                {
                    method: 'DELETE',
                    retryPolicy: 'transient',
                    label: `v2/${this.taskPathSegment}/{taskId}`,
                    headers: { 'Content-Type': 'application/json' },
                    params: { sessionId: request.sessionId ?? this.sessionID },
                    responseTimeoutMs: STATUS_REQUEST_TIMEOUT_MS,
                }
            );
            if (!response.ok) {
                const text = await response.text();
                handleError(
                    new Error(
                        `Failed to delete task result: ${response.status}`
                    ),
                    text
                );
                return false;
            }
            return true;
        } catch (error) {
            this.reportRequestFailure(error, 'Error deleting task result.');
            return false;
        }
    }

    /**
     * Reads the task's status with a single attempt. Throws an
     * {@link HttpStatusError} for an error status and the transport's own
     * error for a request that produced no response; {@link monitorTask}
     * decides which of those are worth polling through.
     */
    private async getTaskStatus(request: RequestParameters, taskId: string) {
        // One attempt: the caller is a polling loop whose own interval is
        // the retry spacing (see `createOutageTolerance`). The per-attempt
        // deadline stays, so a hung read fails and the loop moves on.
        const response = await this.request(
            `v2/${this.taskPathSegment}/${taskId}`,
            {
                retryPolicy: 'none',
                label: `v2/${this.taskPathSegment}/{taskId}`,
                headers: { 'Content-Type': 'application/json' },
                params: { sessionId: request.sessionId ?? this.sessionID },
                responseTimeoutMs: STATUS_REQUEST_TIMEOUT_MS,
            }
        );
        if (!response.ok) {
            throw new HttpStatusError(response.status, 'Task status lookup');
        }
        return await response.json();
    }
}

/**
 * Reads the dataset's `actual_amount` counter from an upload response.
 * Returns undefined when the backend does not report it (older
 * backends, non-JSON bodies); the caller then verifies only uploads
 * that needed a single attempt.
 */
async function readDatasetFileCount(
    response: Response
): Promise<number | undefined> {
    try {
        const payload: unknown = await response.json();
        if (
            typeof payload === 'object' &&
            payload !== null &&
            'actual_amount' in payload &&
            typeof (payload as { actual_amount: unknown }).actual_amount ===
                'number'
        ) {
            return (payload as { actual_amount: number }).actual_amount;
        }
    } catch {
        // Empty or non-JSON success body.
    }
    return undefined;
}
